import React from "react";
import { Link } from "react-router-dom";
import { Search, ShieldCheck, Ship, QrCode, ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { resolveMedia } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { PageHero, SectionHeading } from "@/components/primitives";
import { Reveal } from "@/components/Reveal";
import { Highlight } from "@/components/Highlight";
import VideoPlayer from "@/components/VideoPlayer";
import BuyerForm from "@/components/BuyerForm";

export default function Courtage() {
  const { t, m, lang } = useApp();
  useSEO(
    { fr: "Courtage international de matières premières agricoles | Agrivoire Export", en: "International agricultural commodity brokerage | Agrivoire Export" },
    { fr: "Cacao certifié, noix de cajou, fruits exotiques, caoutchouc et noix de kola : courtage tracé et conforme aux standards internationaux.", en: "Certified cocoa, cashew, exotic fruits, rubber and kola nut: traceable brokerage compliant with international standards." }
  );

  const filieres = [
    { key: "cacao", img: m("filiere_cacao"), video: m("video_cacao"), title: t("courtage", "f_cacao_title"), desc: t("courtage", "f_cacao_desc") },
    { key: "fruits", img: m("filiere_fruits"), video: m("video_fruits"), title: t("courtage", "f_fruits_title"), desc: t("courtage", "f_fruits_desc") },
    { key: "cajou", img: m("filiere_cajou"), video: m("video_cajou"), title: t("courtage", "f_cajou_title"), desc: t("courtage", "f_cajou_desc") },
    { key: "cafe", img: m("filiere_cafe"), video: m("video_cafe"), title: t("courtage", "f_cafe_title"), desc: t("courtage", "f_cafe_desc") },
    { key: "caoutchouc", img: m("filiere_caoutchouc"), video: m("video_caoutchouc"), title: t("courtage", "f_caoutchouc_title"), desc: t("courtage", "f_caoutchouc_desc") },
    { key: "kola", img: m("filiere_kola"), video: m("video_kola"), title: t("courtage", "f_kola_title"), desc: t("courtage", "f_kola_desc"), badge: t("courtage", "f_kola_badge") },
  ];

  const process = [
    { icon: Search, video: m("video_sourcing"), title: t("courtage", "p_1_title"), desc: t("courtage", "p_1_desc") },
    { icon: ShieldCheck, video: m("video_qualite"), title: t("courtage", "p_2_title"), desc: t("courtage", "p_2_desc") },
    { icon: Ship, video: m("video_logistique"), title: t("courtage", "p_3_title"), desc: t("courtage", "p_3_desc") },
    { icon: QrCode, video: m("video_tracabilite"), title: t("courtage", "p_4_title"), desc: t("courtage", "p_4_desc") },
  ];

  return (
    <div data-testid="courtage-page">
      <PageHero kicker={t("courtage", "hero_kicker")} title={<Highlight text={t("courtage", "hero_title")} word={lang === "fr" ? "agricoles" : "agricultural"} className="text-[#8CC63F]" />} image={resolveMedia(m("expertise_courtage"))} />

      <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-24">
        <Reveal>
          <p className="max-w-3xl text-justify font-montserrat text-2xl font-medium leading-snug text-neutral-800 sm:text-3xl">{t("courtage", "intro")}</p>
        </Reveal>
      </section>

      {/* FILIERES */}
      <section className="bg-white py-20 md:py-28" data-testid="filieres-section">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHeading kicker={t("courtage", "filieres_kicker")} title={t("courtage", "filieres_title")} />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filieres.map((f, i) => (
              <Reveal key={f.key} delay={(i % 3) * 0.08}>
                <div className="group h-full overflow-hidden rounded-3xl border border-black/5 bg-[#FAFAFA]" data-testid={`filiere-card-${f.key}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {f.video ? (
                      <VideoPlayer src={f.video} poster={f.img} className="h-full w-full" testId={`filiere-video-${f.key}`} />
                    ) : (
                      <img src={resolveMedia(f.img)} alt={f.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    )}
                    {f.badge && (
                      <span className="absolute left-4 top-4 rounded-full bg-[#FFD700] px-3 py-1 font-poppins text-xs font-bold text-[#0A0A0A]">{f.badge}</span>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-montserrat text-xl font-bold text-neutral-900">{f.title}</h3>
                    <p className="mt-2 font-poppins text-sm leading-relaxed text-neutral-600">{f.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#0A0A0A] py-20 text-white md:py-28" data-testid="process-section">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHeading light kicker={t("courtage", "process_kicker")} title={t("courtage", "process_title")} />
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {process.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="relative h-full rounded-3xl border border-white/10 bg-white/[0.03] p-6" data-testid={`process-step-${i}`}>
                    <span className="font-montserrat text-sm font-bold text-[#8CC63F]">0{i + 1}</span>
                    <Icon className="mt-4 h-9 w-9 text-[#1CADE4]" />
                    <h3 className="mt-4 font-montserrat text-lg font-bold">{p.title}</h3>
                    <p className="mt-2 font-poppins text-sm text-white/65">{p.desc}</p>
                    {p.video && (
                      <VideoPlayer src={p.video} className="mt-4 aspect-video w-full overflow-hidden rounded-xl" testId={`process-video-${i}`} />
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* POUR QUI */}
      <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28" data-testid="courtage-forwho">
        <SectionHeading kicker={t("courtage", "forwho_kicker")} title="" />
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {[
            { title: t("courtage", "forwho_buyers_title"), desc: t("courtage", "forwho_buyers_desc"), c: "#1CADE4" },
            { title: t("courtage", "forwho_producers_title"), desc: t("courtage", "forwho_producers_desc"), c: "#F5821F" },
          ].map((b, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="h-full rounded-3xl border border-black/5 bg-white p-8">
                <span className="block h-1.5 w-12 rounded-full" style={{ backgroundColor: b.c }} />
                <h3 className="mt-5 font-montserrat text-2xl font-bold text-neutral-900">{b.title}</h3>
                <p className="mt-3 font-poppins text-base text-neutral-600">{b.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Link to="/contact#buyer-form" data-testid="courtage-cta-offer" className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#8CC63F] to-[#4E7D2C] px-8 py-4 font-poppins text-base font-semibold text-white shadow-md transition-transform duration-300 hover:scale-105">
              {t("courtage", "cta_offer_commercial")} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact#buyer-form" data-testid="courtage-cta-supplier" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#F5821F] px-8 py-4 font-poppins text-base font-semibold text-[#F5821F] transition-colors duration-300 hover:bg-[#F5821F] hover:text-white">
              {t("courtage", "cta_become_supplier")}
            </Link>
          </div>
        </Reveal>
      </section>
      <section id="buyer-form" className="scroll-mt-24 bg-white py-20 md:py-28" data-testid="courtage-form-section">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <Reveal>
            <p className="max-w-2xl font-montserrat text-2xl font-bold leading-snug text-neutral-900 sm:text-3xl" data-testid="courtage-form-cta">
              {t("courtage", "form_cta")}
            </p>
          </Reveal>
          <div className="mt-10"><BuyerForm /></div>
        </div>
      </section>
    </div>
  );
}
